import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Tooltip } from 'antd';
import PropTypes from 'prop-types';

// How long the pointer must rest on a row before the full name pops up.
// Long enough that a user clicking straight through the tree never sees
// it flash; short enough that a deliberate hover feels immediate.
export const FULL_NAME_HOVER_DELAY_MS = 450;

const CLIPPING_OVERFLOW = /hidden|clip|auto|scroll/;

// Right-hand edge of the row's actual glyphs (not the span, which is
// stretched to the row width). Falls back to the span's own box where
// Range measurement is unavailable (jsdom).
const measureTextRight = (host) => {
  const doc = host.ownerDocument;
  if (doc && typeof doc.createRange === 'function') {
    try {
      const range = doc.createRange();
      range.selectNodeContents(host);
      const rect = range.getBoundingClientRect();
      if (rect && rect.width > 0) return rect.right;
    } catch (e) {
      // fall through to the element box
    }
  }
  const rect = host.getBoundingClientRect();
  return rect ? rect.right : 0;
};

// True when the row's name is not fully visible. Two ways that happens in
// the matter sidebar, and we must catch both:
//
// 1. A wrapper with `text-overflow: ellipsis` is cutting it (`.ant-tree-title`
//    from TreeComponentStyled, or search mode's TitleWrapper nested inside).
//    Detected by `scrollWidth > clientWidth` on the host, that title cell,
//    or any descendant.
// 2. The row itself runs past the sidebar and the SIDEBAR's `overflow-x:
//    hidden` is what chops the text (no ellipsis at all — the name just
//    stops at the panel edge, mid-word). The title cell is NOT overflowing
//    in that case, so we additionally compare the right edge of the text
//    against every overflow-clipping ancestor.
export const isRowNameClipped = (host) => {
  if (!host || typeof host.querySelectorAll !== 'function') return false;

  const candidates = [host];
  const treeTitle =
    typeof host.closest === 'function' ? host.closest('.ant-tree-title') : null;
  if (treeTitle) candidates.push(treeTitle);
  candidates.push(...host.querySelectorAll('*'));
  const ellipsised = candidates.some(
    (el) =>
      el &&
      typeof el.scrollWidth === 'number' &&
      typeof el.clientWidth === 'number' &&
      el.clientWidth > 0 &&
      el.scrollWidth > el.clientWidth + 1
  );
  if (ellipsised) return true;

  if (typeof host.getBoundingClientRect !== 'function') return false;
  const view = host.ownerDocument && host.ownerDocument.defaultView;
  if (!view || typeof view.getComputedStyle !== 'function') return false;
  const textRight = measureTextRight(host);
  if (!textRight) return false;

  let el = host.parentElement;
  while (el && el !== host.ownerDocument.body) {
    const style = view.getComputedStyle(el);
    if (CLIPPING_OVERFLOW.test(style.overflowX) || CLIPPING_OVERFLOW.test(style.overflow)) {
      const rect = el.getBoundingClientRect();
      if (rect && rect.width > 0 && textRight > rect.right + 1) return true;
    }
    el = el.parentElement;
  }
  return false;
};

// Hover-to-reveal full name for a tree row, built so it can never get in
// the way of the mouse:
//
// - Fully controlled. antd's own hover trigger is switched off
//   (`trigger={[]}`) so the Tooltip attaches NO handlers to the row; the
//   only listeners are the ones below, and none of them call
//   preventDefault or stopPropagation, so left-click (open), right-click
//   (context menu), double-click, and native drag all reach their owners
//   exactly as before.
// - Only shows after the pointer rests on the row for
//   FULL_NAME_HOVER_DELAY_MS, and only if the name is actually clipped.
//   Short names never pop anything.
// - Any mouse button going down, a right-click, or a drag starting hides
//   it immediately and disarms the timer, so it is never sitting on top of
//   a context menu, a drag ghost, or the document that just opened. It
//   re-arms only after the pointer leaves the row and comes back.
// - The popup itself is `pointer-events: none`, so even while visible it
//   cannot swallow a click aimed at whatever is underneath it.
const TreeNodeFullName = ({
  name,
  children,
  onMouseEnter,
  onMouseLeave,
  onMouseDown,
  onContextMenu,
  onDragStart,
  ...spanProps
}) => {
  const [open, setOpen] = useState(false);
  const hostRef = useRef(null);
  const timerRef = useRef(null);
  const hasName = typeof name === 'string' && name.length > 0;

  const disarm = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const hide = useCallback(() => {
    disarm();
    setOpen(false);
  }, [disarm]);

  useEffect(() => disarm, [disarm]);

  const handleMouseEnter = (event) => {
    if (onMouseEnter) onMouseEnter(event);
    if (!hasName) return;
    disarm();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      if (isRowNameClipped(hostRef.current)) setOpen(true);
    }, FULL_NAME_HOVER_DELAY_MS);
  };

  const handleMouseLeave = (event) => {
    if (onMouseLeave) onMouseLeave(event);
    hide();
  };

  const handleMouseDown = (event) => {
    if (onMouseDown) onMouseDown(event);
    hide();
  };

  const handleContextMenu = (event) => {
    if (onContextMenu) onContextMenu(event);
    hide();
  };

  const handleDragStart = (event) => {
    hide();
    if (onDragStart) onDragStart(event);
  };

  return (
    <Tooltip
      open={hasName && open}
      trigger={[]}
      title={name}
      placement="rightTop"
      mouseEnterDelay={0}
      mouseLeaveDelay={0}
      styles={{ root: { pointerEvents: 'none', maxWidth: 480 } }}
    >
      <span
        ref={hostRef}
        {...spanProps}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
        onContextMenu={handleContextMenu}
        onDragStart={handleDragStart}
      >
        {children}
      </span>
    </Tooltip>
  );
};

TreeNodeFullName.propTypes = {
  name: PropTypes.string,
  children: PropTypes.node,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onMouseDown: PropTypes.func,
  onContextMenu: PropTypes.func,
  onDragStart: PropTypes.func,
};

export default TreeNodeFullName;
